/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/ScopePricelistFastAdd.tsx
 * FILE_ID: QB-AGGRID-FAST-ADD-002
 * PURPOSE: Keyboard-first pricelist fast-add combobox for the scope grid.
 * WHY: Estimators should add catalogue products with type -> Enter -> qty,
 *      without opening a side panel or losing their place in the grid.
 * FLOW: load default pricebook -> local suggestions -> pick -> quote row prefill.
 * RELATED: pricelist-fast-add.ts, ScopeAgGridSection.tsx
 */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Loader2, PackageSearch, X } from 'lucide-react'
import { createPricelistClient } from '@quotech/pricelist'
import type {
  PriceBookSummary,
  PricelistItem,
  PricelistSection,
} from '@quotech/pricelist'
import type { PriceBookItem } from '@/lib/api/pricing-api/types'
import { getApiClient } from '@/lib/setupApi'
import {
  buildFastAddOptions,
  filterFastAddOptions,
  type FastAddOption,
} from './pricelist-fast-add'

export interface ScopePricelistFastAddHandle {
  focus: () => void
}

export interface ScopePricelistFastAddProps {
  isEditable: boolean
  onPickItem: (item: PriceBookItem) => void
  onOpenDrawer: () => void
}

function optionLabel(option: FastAddOption): string {
  return [option.code, option.name].filter(Boolean).join(' · ')
}

const ScopePricelistFastAdd = forwardRef<
  ScopePricelistFastAddHandle,
  ScopePricelistFastAddProps
>(function ScopePricelistFastAdd({ isEditable, onPickItem, onOpenDrawer }, ref) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listId = 'scope-pricelist-fast-add-results'
  const client = useMemo(
    () => createPricelistClient(getApiClient(), '/quotebuilder'),
    [],
  )
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [options, setOptions] = useState<FastAddOption[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const suggestions = useMemo(
    () => filterFastAddOptions(options, query),
    [options, query],
  )
  const showSuggestions = isEditable && query.trim().length >= 2

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
  }))

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    client
      .listPricebooks()
      .then(async (books: PriceBookSummary[]) => {
        const pricebookId = books.find((b) => b.is_default)?.id ?? books[0]?.id
        if (!pricebookId) return { sections: [], items: [] }
        const [sections, items] = await Promise.all([
          client.listSections(pricebookId),
          client.listItems(pricebookId),
        ])
        return { sections, items }
      })
      .then(
        ({
          sections,
          items,
        }: {
          sections: PricelistSection[]
          items: PricelistItem[]
        }) => {
          if (!alive) return
          setOptions(buildFastAddOptions(items, sections))
          setLoading(false)
        },
      )
      .catch(() => {
        if (!alive) return
        setError('Pricelist unavailable')
        setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [client])

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  const pick = (option: FastAddOption) => {
    onPickItem(option.priceBookItem)
    setQuery('')
    setActiveIndex(0)
  }

  return (
    <div className="relative flex min-w-[280px] flex-1 items-center gap-1">
      <PackageSearch className="h-3.5 w-3.5 text-slate-400" />
      <input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-label="Fast-add product from pricelist"
        aria-expanded={showSuggestions && suggestions.length > 0}
        aria-controls={listId}
        aria-activedescendant={
          showSuggestions && suggestions[activeIndex]
            ? `${listId}-${suggestions[activeIndex].id}`
            : undefined
        }
        disabled={!isEditable}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && showSuggestions) {
            event.preventDefault()
            setActiveIndex((idx) => Math.min(idx + 1, suggestions.length - 1))
          } else if (event.key === 'ArrowUp' && showSuggestions) {
            event.preventDefault()
            setActiveIndex((idx) => Math.max(idx - 1, 0))
          } else if (event.key === 'Enter' && showSuggestions) {
            const option = suggestions[activeIndex]
            if (!option) return
            event.preventDefault()
            pick(option)
          } else if (event.key === 'Escape') {
            setQuery('')
            setActiveIndex(0)
          }
        }}
        placeholder="Fast-add product by code or name..."
        className="h-7 w-full rounded border border-slate-300 bg-white px-2 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
      />
      {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
      {query && !loading && (
        <button
          type="button"
          aria-label="Clear fast-add search"
          onClick={() => {
            setQuery('')
            inputRef.current?.focus()
          }}
          className="flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        type="button"
        onClick={onOpenDrawer}
        className="whitespace-nowrap rounded px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
      >
        Browse
      </button>

      {showSuggestions && (
        <div
          id={listId}
          role="listbox"
          className="absolute left-5 right-12 top-8 z-30 max-h-64 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900"
        >
          {error ? (
            <div className="px-3 py-2 text-xs text-red-600 dark:text-red-400">
              {error}. Use Browse instead.
            </div>
          ) : suggestions.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500">
              No pricelist match. Use Browse for the full workbook.
            </div>
          ) : (
            suggestions.map((option, index) => (
              <button
                key={option.id}
                id={`${listId}-${option.id}`}
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(option)}
                className={`flex w-full items-start gap-2 px-3 py-2 text-left text-xs ${
                  index === activeIndex
                    ? 'bg-sky-50 text-sky-950 dark:bg-sky-950/40 dark:text-sky-100'
                    : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800'
                }`}
              >
                <span className="min-w-[84px] font-mono text-[11px] text-slate-500 dark:text-slate-400">
                  {option.code || 'No code'}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">
                    {optionLabel(option) || option.description || 'Unnamed item'}
                  </span>
                  <span className="block truncate text-[11px] text-slate-500 dark:text-slate-400">
                    {[option.description, option.unit, option.sectionName]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
})

export default ScopePricelistFastAdd
